// Planque : menu à onglets. Équipement (personnage en 3D, emplacements, sac, coffre), Planque (modules, fabrication),
// Marchands, Contrats, Raid (carte et départ).
import * as THREE from 'three';
import { gridValue, maxDur, type Grid, type ItemInst } from '../shared/inventory';
import { CARRY_KG, ITEMS, weightOf } from '../shared/items';
import { MAX_HP } from '../shared/constants';
import { MAP } from '../shared/map';
import type { ClientMsg, Place, ProfileView } from '../shared/protocol';
import { Actor, survivorModel } from './actors';
import { renderGrid, renderSlot, setDropHandler, registerZone, type GridHandlers } from './grid';
import { iconOf } from './icons';
import { countOf } from '../shared/inventory';
import { QUESTS, repLevel, repOf, type Quest } from '../shared/quests';
import { toggleTracked, tracked } from './tracked';
import { BaseScene, MODULE_ICON } from './basescene';
import { DevPanel, Workshop } from './workshop';
import { TIMES, WEATHERS, condEffects } from '../shared/atmos';
import { SocialPanel } from './social';
import { MODULES, RECIPES, craftTime, repairCost, repairedMax, levelFromXp, levelOf, recipeById, xpFor, MAX_LEVEL, type ModuleId } from '../shared/hideout';

const $ = (id: string) => document.getElementById(id)!;
const cr = (n: number) => `${n.toLocaleString('fr-FR')} cr`;
const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

// Les trois factions qui font commerce avec les récupérateurs
const TRADERS = [
  { id: 'comptoir', name: 'Le Comptoir', role: 'Marchand général', bio: 'Rachète tout ce qui a de la valeur, revend le nécessaire. Ne pose pas de questions.', offers: ['w_pistol', 'a_9', 'a_9ap', 'h_hard', 'v_soft', 'm_bandage', 'b_small', 'b_patrol'] },
  { id: 'garde', name: 'La Garde', role: 'Armes et munitions', bio: 'Anciens militaires restés aux portes de la zone. Ils arment ceux qui y entrent, et réservent le meilleur à ceux qui ont fait leurs preuves.', offers: ['w_sg', 'a_12', 'a_556', 'w_smg', 'w_ar', 'a_762', 'w_ak', 'w_dmr', 'w_bolt', 'a_556ap', 'v_police', 'a_762ap', 'h_mil', 'v_mil', 'b_assault', 'b_raid'] },
  { id: 'armurier', name: 'L’Armurier', role: 'Accessoires d’armes', bio: 'Un ancien armurier de la Garde qui bricole viseurs, silencieux et chargeurs dans un atelier caché.', offers: ['gp_vert', 'mz_comp', 'o_reddot', 'mg_ext', 'gp_angle', 'o_holo', 'mz_brake', 'mz_supp', 'o_x4', 'mg_drum'] },
  { id: 'brancardiers', name: 'Les Brancardiers', role: 'Soins', bio: 'Une équipe médicale qui tient un dispensaire de fortune à la lisière.', offers: ['m_bandage', 'm_splint', 'm_kit'] },
];

type Tab = 'gear' | 'base' | 'traders' | 'quests' | 'raid';

// Contrats : portraits des partenaires (formes simples), icônes, illustrations des missions
const PORTRAITS: Record<string, string> = {
  comptoir: '<rect width="120" height="120" fill="#1d1f21"/><path d="M0 120V96l28-14 32 8 32-8 28 14v24z" fill="#5a4636"/><path d="M28 82l32 8 32-8-10 38H38z" fill="#4a3a2d"/><path d="M44 74h32v14l-16 8-16-8z" fill="#b98d6e"/><path d="M38 44l4-14 18-6 18 6 4 14-2 26-20 10-20-10z" fill="#c49a7c"/><path d="M38 60l6 16 16 8 16-8 6-16-4 18-18 10-18-10z" fill="#8a8178"/><path d="M42 66l18 10 18-10-6 12-12 4-12-4z" fill="#9c938a"/><path d="M36 42l2-18 22-10 22 10 2 18-12-6H48z" fill="#3a3d40"/><rect x="35" y="38" width="50" height="7" fill="#2d3032"/><path d="M46 52h9M65 52h9" stroke="#1c1c1c" stroke-width="3"/>',
  garde: '<rect width="120" height="120" fill="#1b1d1f"/><path d="M0 120V94l30-14 30 6 30-6 30 14v26z" fill="#4c5434"/><rect x="34" y="88" width="16" height="14" fill="#3e4530"/><rect x="70" y="88" width="16" height="14" fill="#3e4530"/><path d="M44 72h32v14l-16 6-16-6z" fill="#2a2c2a"/><path d="M38 44l4-12 18-6 18 6 4 12-2 24-20 10-20-10z" fill="#2c2e2c"/><path d="M44 48h32v8H44z" fill="#b98d6e"/><path d="M47 52h8M65 52h8" stroke="#1c1c1c" stroke-width="3"/><path d="M32 44c0-16 12-26 28-26s28 10 28 26l2 4H30z" fill="#55603a"/><path d="M30 46h60v5H30z" fill="#434c2d"/>',
  brancardiers: '<rect width="120" height="120" fill="#1e1f21"/><path d="M0 120V96l30-14 30 6 30-6 30 14v24z" fill="#a9a59a"/><path d="M90 84l22 10-4 18-20-6z" fill="#e5e1d6"/><path d="M98 92h6v12h-6zM95 95h12v6H95z" fill="#c43b30"/><path d="M46 74h28v14l-14 6-14-6z" fill="#c9a184"/><path d="M40 44l4-14 16-6 16 6 4 14-2 24-18 10-18-10z" fill="#d0a888"/><path d="M47 52h8M65 52h8" stroke="#2a1d16" stroke-width="3"/><path d="M38 46c-2-18 8-28 22-28s24 10 22 28l-6-12-16-4-16 4z" fill="#4a3226"/><circle cx="60" cy="16" r="8" fill="#4a3226"/><path d="M38 46l-4 22 8-4zM82 46l4 22-8-4z" fill="#4a3226"/>',
};
const TRADER_ICON: Record<string, string> = {
  comptoir: '<path d="M3.5 8 12 4l8.5 4v8.5L12 20.5l-8.5-4z"/><path d="M3.5 8 12 12l8.5-4M12 12v8.5"/>',
  garde: '<path d="M12 3 5 6v5.5c0 4.4 3 7.8 7 9.5 4-1.7 7-5.1 7-9.5V6z"/>',
  brancardiers: '<path d="M9.5 4h5v5.5H20v5h-5.5V20h-5v-5.5H4v-5h5.5z"/>',
};
const QUEST_ART: Record<string, string> = {
  infected: '<rect width="160" height="100" fill="#1c201d"/><path d="M0 100V70l12-22 10 22 8-30 12 30 10-18 10 18 8-26 10 26 12-20 12 20 10-28 12 28 14-16 10 16V100z" fill="#161917"/><g fill="#2c2f2a"><circle cx="88" cy="46" r="8"/><path d="M78 56h20l4 26h-6l-2 18h-6l-2-12-2 12h-6l-2-18h-6z"/><path d="M98 58l16 6-2 4-16-4zM78 58l-14 10 2 3 14-8z"/></g>',
  bandit: '<rect width="160" height="100" fill="#1d1e20"/><path d="M0 100V78l20-12 18 10 22-16 30 18 26-12 44 20v14z" fill="#151617"/><g fill="#2d2f2c"><circle cx="76" cy="40" r="9"/><path d="M62 52h28l4 30h-8l-2 18h-8l-2-12-2 12h-8l-2-18h-8z"/><path d="M84 58l40-6 1 4-38 8z" fill="#202224"/></g><rect x="70" y="38" width="13" height="4" fill="#c49a7c" opacity="0.7"/>',
  boss: '<rect width="160" height="100" fill="#1a1c1e"/><path d="M0 100V80l30-8 30 8 30-10 30 8 40-6v28z" fill="#131416"/><g fill="#26292a"><path d="M94 100l8-56h4l8 56M100 70h12" stroke="#26292a" stroke-width="3"/><rect x="92" y="30" width="28" height="16"/><path d="M88 30l18-12 18 12z"/></g><rect x="96" y="34" width="20" height="6" fill="#e8a33d" opacity="0.8"/><circle cx="106" cy="37" r="22" fill="#e8a33d" opacity="0.08"/>',
};
const svgIcon = (d: string, sw = 1.8) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const QI = {
  target: svgIcon('<circle cx="12" cy="12" r="7.5"/><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>'),
  check: svgIcon('<path d="M5 12.5l4.5 4.5L19 7.5"/>', 2.4),
  lock: svgIcon('<rect x="5" y="10.5" width="14" height="10" rx="1.5"/><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5"/>'),
  clock: svgIcon('<path d="M20 12a8 8 0 1 1-2.3-5.6M20 4v4h-4"/>'),
  pin: svgIcon('<path d="M12 21s-6-6.3-6-11a6 6 0 0 1 12 0c0 4.7-6 11-6 11z"/><circle cx="12" cy="10" r="2"/>'),
  gear: svgIcon('<circle cx="12" cy="12" r="3"/><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1"/>'),
  cash: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 9l10-4 10 4-10 4z" fill="#8a9a5a"/><path d="M2 9v3l10 4 10-4V9l-10 4z" fill="#6c7a44"/><path d="M2 12v3l10 4 10-4v-3l-10 4z" fill="#58653a"/></svg>',
  hand: svgIcon('<path d="M3 13l4-4 4 2 3-3 4 2 3 4-6 6-5-1-3-3z"/><path d="M11 11l3 3M9 13l3 3"/>'),
  flag: svgIcon('<path d="M6 21V4M6 4h11l-2 4 2 4H6"/>'),
  xp: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2 21 7v10l-9 5-9-5V7z" fill="none" stroke="currentColor" stroke-width="1.6"/><text x="12" y="15.5" text-anchor="middle" font-family="Barlow Condensed, Arial Narrow, sans-serif" font-weight="700" font-size="8" fill="currentColor">XP</text></svg>',
};
const ROMAN = ['', 'I', 'II', 'III', 'IV'];
type QState = 'done' | 'active' | 'locked' | 'open';


// Carte du raid : icône de chaque lieu nommé
const MAP_ICONS: Record<string, string> = {
  cart: '<path d="M3 4h2.4l2.2 10.2h10.2L20 7.5H6.2"/><circle cx="9" cy="18.5" r="1.4"/><circle cx="17" cy="18.5" r="1.4"/>',
  house: '<path d="M3 11.5 12 4l9 7.5"/><path d="M5.5 10v10h13V10"/><path d="M10 20v-5h4v5"/>',
  cross: '<path d="M9.5 4h5v5.5H20v5h-5.5V20h-5v-5.5H4v-5h5.5z"/>',
  columns: '<path d="M3 9 12 4l9 5H3zM5 9v9M9.5 9v9M14.5 9v9M19 9v9M3 20h18"/>',
  shield: '<path d="M12 3 5 6v5.5c0 4.4 3 7.8 7 9.5 4-1.7 7-5.1 7-9.5V6z"/><path d="M9.5 12l2 2 3.5-4"/>',
  warehouse: '<path d="M3 9.5 12 4l9 5.5V20H3z"/><path d="M7 20v-7h10v7M7 16h10"/>',
  wheat: '<path d="M12 21V8M12 8c-2-1-3-3-3-5 2 0 3 1.5 3 3.5 0-2 1-3.5 3-3.5 0 2-1 4-3 5zM12 13c-2.5 0-4-1.5-4.5-3.5 2.2 0 3.8 1 4.5 3 .7-2 2.3-3 4.5-3-.5 2-2 3.5-4.5 3.5z"/>',
  tower: '<path d="M7 21 9 9M17 21 15 9M8 15h8M6 9h12V5l-6-2-6 2z"/>',
  wrench: '<path d="M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3l7.5-7.5a4 4 0 0 0-2-2z"/><path d="M4 4l4 4"/>',
  fuel: '<path d="M4 20V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v15M3 20h12M6 8h6M14 10h2a2 2 0 0 1 2 2v4a1.5 1.5 0 0 0 3 0V9l-3-3"/>',
  book: '<path d="M4 5.5C6.5 4 9.5 4 12 6c2.5-2 5.5-2 8-.5V19c-2.5-1.5-5.5-1.5-8 .5-2.5-2-5.5-2-8-.5z"/><path d="M12 6v13.5"/>',
  tractor: '<circle cx="7" cy="16" r="3.5"/><circle cx="18" cy="17.5" r="2"/><path d="M7 12.5V7h5l2 5.5h4.5V15M10.5 16h5.5"/>',
  barn: '<path d="M3 10 7 5h10l4 5v10H3z"/><path d="M9 20v-6h6v6M9 14l6 6M15 14l-6 6"/>',
  cabin: '<path d="M3 12 12 5l9 7"/><path d="M5 11v9h14v-9M5 14h14M5 17h14"/>',
  church: '<path d="M12 2v5M10 4h4M6 21v-9l6-5 6 5v9zM10 21v-4h4v4"/>',
  glass: '<path d="M6 4h12l-1.5 7a4.5 4.5 0 0 1-9 0zM12 15.5V20M8.5 20h7"/>',
  mill: '<circle cx="12" cy="12" r="2.2"/><path d="M12 9.8V3M14.2 12H21M12 14.2V21M9.8 12H3M12 3l3 3M21 12l-3 3M12 21l-3-3M3 12l3-3"/>',
  tent: '<path d="M3 20 12 5l9 15zM12 5v15M9 20l3-5 3 5"/>',
  radio: '<path d="M12 21v-9M8 21h8M8.5 9.5a5 5 0 0 1 7 0M6 7a8.5 8.5 0 0 1 12 0"/><circle cx="12" cy="12" r="1.3"/>',
  bed: '<path d="M3 7v13M3 14h18v6M21 14v-2.5a3 3 0 0 0-3-3h-7v5.5M6.5 11.5h.01"/>',
  ammo: '<path d="M5 20V9l2-4 2 4v11zM11 20V9l2-4 2 4v11zM17 20V9l2-4 2 4v11z"/>',
  pin: '<path d="M12 21s-6-6.3-6-11a6 6 0 0 1 12 0c0 4.7-6 11-6 11z"/><circle cx="12" cy="10" r="2"/>',
};
const NAME_ICON: Record<string, string> = {
  'Supérette': 'cart',
  'Maison des Lilas': 'house',
  'Pharmacie': 'cross',
  'Mairie': 'columns',
  'Commissariat': 'shield',
  'École Jean-Moulin': 'book',
  'Garage Moreau': 'wrench',
  'Entrepôt Duval': 'warehouse',
  'Coopérative': 'wheat',
  'Poste de garde': 'tower',
  'Station-service': 'fuel',
  'Infirmerie du camp': 'cross',
  'Poste de commandement': 'radio',
  'Dortoir': 'bed',
  'Armurerie du camp': 'ammo',
  'Ferme des Combes': 'tractor',
  'Grange': 'barn',
  'Hangar': 'warehouse',
  'Cabane de chasse': 'cabin',
  'Cabane du garde': 'cabin',
  'Chapelle Saint-Roch': 'church',
  'Bar-tabac du Moulin': 'glass',
  'Moulin': 'mill',
  'QG de la base': 'shield',
  'Hangar nord': 'warehouse',
  'Hangar sud': 'warehouse',
  'Casernement': 'bed',
  'Bunker': 'ammo',
  'Poste de contrôle': 'tower',
};
const RUN_ICON = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="14.5" cy="4" r="2"/><path d="M13 7.5 9 9.5l-2 4 1.8.8 1.5-3 1.8-.7-1.6 5.2-3.5 4.2 1.6 1.3 3.7-4.4 2.1 2.1V24h2v-6l-2.6-2.8.9-3.1 1 1.9 3.6 1.3.6-1.9-2.8-1-1.8-3.4c-.4-.8-1.4-1.2-2.3-.9z"/></svg>';


const ico = (d: string) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
const VITAL = {
  kg: ico('<path d="M8 8a4 4 0 0 1 8 0"/><path d="M5 9h14l1.5 11h-17z"/>'),
  hp: ico('<path d="M12 20s-7-4.3-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.7-7 10-7 10z"/>'),
  shield: ico('<path d="M12 3 5 6v5.5c0 4.4 3 7.8 7 9.5 4-1.7 7-5.1 7-9.5V6z"/>'),
  coin: ico('<circle cx="12" cy="12" r="8"/><path d="M14.5 9.2c-.6-.8-1.5-1.2-2.5-1.2-1.6 0-2.8.9-2.8 2.1 0 2.8 5.6 1.4 5.6 4 0 1.2-1.2 2.1-2.8 2.1-1.1 0-2.1-.5-2.6-1.3M12 6.5V8M12 16v1.5"/>'),
};

export class Hideout {
  private profile: ProfileView | null = null;
  get xp() { return this.profile?.xp ?? 0; } // expérience connue avant le raid
  get loadout() { return this.profile?.loadout ?? null; }
  private prices: Record<string, number> = {};
  private trader = 0;
  private sellArmed: { uid: number; until: number } | null = null;
  private tab: Tab = 'gear';
  private qFilter: 'all' | 'done' | 'locked' = 'all';
  private qTrader: string | null = null;
  private qSel: string | null = null;
  private unlock: Record<string, number> = {};
  private clock = 0; // heure du serveur - heure locale (ms)
  private base: BaseScene | null = null; // planque en 3D
  private baseSel: ModuleId = 'workbench';
  private baseAll = false; // ingrédients des recettes affichés
  private workshop: Workshop;
  private devPanel: DevPanel;
  // Amis, groupe, recherche de raid ; heure et météo du serveur
  readonly social: SocialPanel;
  private sQuery = ''; // coffre : recherche et catégorie affichées (les autres objets sont estompés)
  private sCat = '';
  private preview: { renderer: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.PerspectiveCamera; actor: Actor | null; yaw: number; drag: number | null } | null = null;

  constructor(private send: (m: ClientMsg) => void, private toast: (m: string) => void) {
    this.workshop = new Workshop(send, () => this.profile);
    this.social = new SocialPanel(send);
    this.social.onChange = () => this.renderRaid();
    this.devPanel = new DevPanel(send);
    $('hoDev').onclick = () => this.devPanel.toggle();
    document.querySelectorAll<HTMLButtonElement>('#hoTabs button').forEach(b => { b.onclick = () => this.setTab(b.dataset.tab as Tab); });
    // Sous-onglets du personnage : équipement (personnage en 3D) ou statistiques
    $('charSub').onclick = e => {
      const b = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
      if (!b) return;
      document.querySelectorAll('#charSub button').forEach(x => x.classList.toggle('on', x === b));
      $('hoStats').classList.toggle('hidden', b.dataset.sub !== 'stats');
    };
    // Coffre : recherche, catégories, rangement automatique
    ($('stashSearch') as HTMLInputElement).oninput = e => { this.sQuery = (e.target as HTMLInputElement).value.trim().toLowerCase(); this.filterStash(); };
    $('stashCats').onclick = e => {
      const b = (e.target as HTMLElement).closest('[data-cat]') as HTMLElement | null;
      if (!b) return;
      this.sCat = b.dataset.cat!;
      document.querySelectorAll('#stashCats button').forEach(x => x.classList.toggle('on', x === b));
      this.filterStash();
    };
    $('stashSort').onclick = () => this.send({ t: 'sort' });
    $('baseDetail').onclick = e => {
      const b = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
      if (!b || b.disabled) return;
      if (b.dataset.all) { this.baseAll = !this.baseAll; this.render(); return; }
      if (b.dataset.up) this.send({ t: 'upgrade', module: b.dataset.up });
      else if (b.dataset.craft) this.send({ t: 'craft', recipe: b.dataset.craft });
      else if (b.dataset.collect) this.send({ t: 'collect', module: b.dataset.collect });
    };
    // Fabrications en cours : barres et comptes à rebours mis à jour chaque seconde
    setInterval(() => { this.tickCrafts(); this.tickQueue(); }, 1000);
    // Contrats : sélection d'une mission, filtres, partenaire, actions de la fiche
    $('questList').onclick = e => {
      const row = (e.target as HTMLElement).closest('[data-q]') as HTMLElement | null;
      if (row) { this.qSel = row.dataset.q!; this.render(); }
    };
    $('qTabs').onclick = e => {
      const b = (e.target as HTMLElement).closest('[data-f]') as HTMLElement | null;
      if (b) { this.qFilter = b.dataset.f as 'all' | 'done' | 'locked'; this.render(); }
    };
    ($('qSort') as HTMLSelectElement).onchange = () => this.render();
    $('qPartners').onclick = e => {
      const b = (e.target as HTMLElement).closest('[data-trader]') as HTMLElement | null;
      if (b) { this.qTrader = this.qTrader === b.dataset.trader ? null : b.dataset.trader!; this.render(); }
    };
    $('qDetail').onclick = e => {
      const b = (e.target as HTMLElement).closest('[data-act]') as HTMLButtonElement | null;
      if (!b || b.disabled || !this.qSel) return;
      if (b.dataset.act === 'track') { toggleTracked(this.qSel); this.render(); }
      else this.send({ t: 'quest', id: this.qSel, act: b.dataset.act as 'accept' | 'turnin' });
    };
    // Raccourcis de l'onglet Contrats : F suivre, Espace remettre ou accepter, Échap retour à l'équipement
    addEventListener('keydown', e => {
      if (this.tab !== 'quests' || $('hideout').classList.contains('hidden') || (e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'SELECT') return;
      if (e.code === 'KeyF' && this.qSel) { toggleTracked(this.qSel); this.render(); }
      else if (e.code === 'Space') { e.preventDefault(); ($('qDetail').querySelector('[data-act=turnin]:not(:disabled), [data-act=accept]') as HTMLButtonElement | null)?.click(); }
      else if (e.code === 'Escape') this.setTab('gear');
    });
    addEventListener('keydown', e => {
      if (this.tab === 'base' && e.code === 'Escape' && !$('hideout').classList.contains('hidden')) this.setTab('gear');
    });
    // Raid : recherche (seul, ou le chef pour son groupe) ou annulation
    $('raidGo').onclick = () => this.send({ t: 'queue', act: this.social.state?.queue ? 'cancel' : 'start' });
    $('raidGroup').onclick = e => { if ((e.target as HTMLElement).closest('[data-open-friends]')) this.social.toggle(true); };
    $('shop').onclick = e => {
      const b = (e.target as HTMLElement).closest('[data-buy]') as HTMLElement | null;
      if (b) this.send({ t: 'buy', id: b.dataset.buy! });
    };
    $('traderList').onclick = e => {
      const b = (e.target as HTMLElement).closest('[data-trader]') as HTMLElement | null;
      if (b) { this.trader = Number(b.dataset.trader); this.render(); }
    };
    registerZone($('sellZone'), 'sell');
    this.drawMap();
  }

  show(p: ProfileView, prices: Record<string, number>, unlock: Record<string, number> = {}, serverNow = Date.now(), dev = false) {
    this.profile = p;
    // Mode test (serveur local) : bouton dans la barre du haut, F8
    if (dev !== this.devPanel.enabled) { this.devPanel.enabled = dev; this.devPanel.refreshIcons(); }
    $('hoDev').classList.toggle('hidden', !dev);
    this.workshop.refresh();
    this.prices = prices;
    this.unlock = unlock;
    this.clock = serverNow - Date.now();
    $('hideout').classList.remove('hidden');
    // Glisser-déposer dans la planque : déplacer, ou vendre en lâchant sur la zone de vente
    setDropHandler((it, from, to, x, y) => {
      if (from === 'cont' || to === 'cont') return;
      if (to === 'sell') this.send({ t: 'sell', from: from as Place, uid: it.uid });
      else this.send({ t: 'move', from: from as Place, uid: it.uid, to: to as Place, x, y });
    });
    this.render();
    this.startPreview();
  }
  hide() { $('hideout').classList.add('hidden'); }

  // Onglet Raid : heure et météo du serveur (et le prochain changement), groupe, recherche en cours
  private renderRaid() {
    const st = this.social.state;
    if (!st) return;
    const ICO: Record<string, string> = {
      day: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>',
      dusk: '<path d="M3 17h18M6 17a6 6 0 0 1 12 0M12 7V4M5.6 9.6 3.5 7.5M18.4 9.6l2.1-2.1M4 21h16"/>',
      night: '<path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z"/>',
      clear: '<circle cx="12" cy="12" r="4"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2"/>',
      overcast: '<path d="M7 18h10a4 4 0 0 0 .5-7.97A6 6 0 0 0 6 11a3.5 3.5 0 0 0 1 7z"/>',
      rain: '<path d="M7 14h10a4 4 0 0 0 .5-7.97A6 6 0 0 0 6 7a3.5 3.5 0 0 0 1 7zM8 17l-1 3M12 17l-1 3M16 17l-1 3"/>',
      fog: '<path d="M4 9h16M2 13h18M5 17h14M7 5h10"/>',
    };
    const chip = (id: string, name: string) => `<span class="chip"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICO[id]}</svg>${name}</span>`;
    const c = st.cond.now, t = TIMES.find(x => x.id === c.time)!, w = WEATHERS.find(x => x.id === c.weather)!;
    const nt = TIMES.find(x => x.id === st.cond.next.time)!, nw = WEATHERS.find(x => x.id === st.cond.next.weather)!;
    const mins = Math.max(1, Math.round((st.cond.changeAt - (Date.now() + this.clock)) / 60000));
    $('condNow').innerHTML = `${chip(t.id, t.name)}${chip(w.id, w.name)}<small>Dans ${mins} min : ${nt.name.toLowerCase()}, ${nw.name.toLowerCase()}</small>`;
    const fx = condEffects(c);
    const bits = [t.note, w.id !== 'clear' ? w.note : ''].filter(Boolean);
    const stats = [`vue des ennemis ${Math.round(fx.sight * 100)} %`, fx.hearing !== 1 ? `ouïe ${Math.round(fx.hearing * 100)} %` : '', fx.horde !== 1 ? `infectés +${Math.round((fx.horde - 1) * 100)} %` : '', fx.loot ? `butin +${Math.round(fx.loot * 100)} %` : ''].filter(Boolean);
    $('condNote').innerHTML = `${bits.join(' ')}<br><small>${stats.join(' · ')} · mêmes conditions pour tous les joueurs</small>`;
    // Groupe
    const g = st.group, lead = !g || g.leader === st.me;
    $('raidGroup').innerHTML = g
      ? g.members.map(f => `<div class="socRow"><span class="socWho">${f.avatar ? `<img src="${esc(f.avatar)}" alt="">` : '<i class="socAva"></i>'}<span><b>${esc(f.name)}</b><small><i class="dot ${f.status}"></i>${f.pid === g.leader ? 'Chef du groupe' : 'Membre'} · niv. ${f.level}</small></span></span></div>`).join('')
        + (g.invited.length ? `<p class="hint">${g.invited.length} invitation${g.invited.length > 1 ? 's' : ''} en attente.</p>` : '')
      : '<p class="hint">Seul. <a href="#" data-open-friends>Inviter des amis</a> (3 joueurs au plus) : le groupe entre dans le même raid, au même endroit.</p>';
    // Recherche
    const btn = $('raidGo') as HTMLButtonElement;
    btn.disabled = !lead;
    btn.textContent = st.queue ? 'Annuler la recherche' : lead ? (g ? 'Rechercher un raid (groupe)' : 'Rechercher un raid') : 'Le chef lance la recherche';
    btn.classList.toggle('searching', !!st.queue);
    this.tickQueue();
  }
  private tickQueue() {
    const q = this.social.state?.queue;
    if (!q) { $('queueInfo').textContent = ''; return; }
    const s = Math.max(0, Math.floor((Date.now() + this.clock - q.since) / 1000));
    $('queueInfo').textContent = `Recherche en cours… ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} · ${q.searching} joueur${q.searching > 1 ? 's' : ''} en recherche`;
  }

  private setTab(t: Tab) {
    this.tab = t;
    document.querySelectorAll<HTMLButtonElement>('#hoTabs button').forEach(b => b.classList.toggle('on', b.dataset.tab === t));
    for (const id of ['gear', 'base', 'traders', 'quests', 'raid']) $(`tab-${id}`).classList.toggle('hidden', id !== t);
    // Raccourcis rappelés en bas de l'écran, selon l'onglet
    $('hubKeys').innerHTML = t === 'quests'
      ? '<kbd>LMB</kbd>Sélectionner<kbd>F</kbd>Suivre / ne plus suivre<kbd>Espace</kbd>Remettre ou accepter<kbd>Échap</kbd>Retour'
      : t === 'base' ? '<kbd>LMB</kbd>Sélectionner / interagir<kbd>Glisser</kbd>Tourner la vue<kbd>Molette</kbd>Zoom<kbd>Échap</kbd>Retour'
      : '<kbd>LMB</kbd>Glisser : déplacer<kbd>LMB</kbd>Clic : équiper ou ranger<kbd>Maj + LMB</kbd>Sac ↔ poche<kbd>RMB ×2</kbd>Vendre<kbd>Ctrl + LMB</kbd>Réparer<kbd>Survol</kbd>Examiner';
    this.render();
  }

  // Arme équipée : bouton vers l'atelier d'armes (accessoires)
  private modButton(slot: HTMLElement, from: Place, it: ItemInst | null) {
    if (!it || it.free || !ITEMS[it.id]!.weapon) return;
    const b = document.createElement('button');
    b.className = 'modBtn';
    b.title = 'Atelier d’armes : accessoires (Ctrl + clic sur une arme)';
    b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1"/></svg>Modifier';
    b.onpointerdown = e => e.stopPropagation();
    b.onclick = e => { e.stopPropagation(); this.workshop.open(from, it.uid); };
    slot.append(b);
  }

  // Protection portée et usée : bouton « Réparer » avec son prix (rouge si pas assez de crédits)
  private repairButton(slot: HTMLElement, from: Place, it: ItemInst | null) {
    const d = it && ITEMS[it.id], p = this.profile;
    if (!it || !d?.armor || !p) return;
    const max = maxDur(it), cost = repairCost(p.hideout, it.dur ?? 0, max, d.armor.dur, d.value);
    const after = repairedMax(it.dur ?? 0, max, d.armor.dur);
    if (!cost) return;
    const b = document.createElement('button');
    b.className = `repBtn${p.credits < cost ? ' miss' : ''}`;
    b.title = `Réparer : ${Math.round(it.dur ?? 0)} → ${Math.round(after)} (durabilité max perdue : ${Math.round((max - after) * 10) / 10}). Ctrl + clic sur une protection du coffre.`;
    b.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3l7.5-7.5a4 4 0 0 0-2-2z"/></svg>${cost.toLocaleString('fr-FR')} cr`;
    b.onpointerdown = e => e.stopPropagation();
    b.onclick = e => { e.stopPropagation(); this.send({ t: 'repair', from, uid: it.uid }); };
    slot.append(b);
  }

  // Clic : vers l'autre côté (coffre ↔ équipement). Maj + clic : sac ↔ poche sécurisée. Clic droit : vendre.
  private handlers(from: Place): GridHandlers {
    return {
      click: (it: ItemInst, e: MouseEvent) => {
        const d = ITEMS[it.id]!;
        let to: Place;
        // Ctrl + clic : arme → atelier d'armes ; protection usée → réparation
        if ((e.ctrlKey || e.metaKey) && ITEMS[it.id]!.weapon && !it.free) { this.workshop.open(from, it.uid); return; }
        if ((e.ctrlKey || e.metaKey) && ITEMS[it.id]!.armor) { this.send({ t: 'repair', from, uid: it.uid }); return; }
        if (from === 'stash') to = d.cat === 'bag' ? 'backpack' : d.armor ? d.armor.slot : d.cat === 'weapon' ? (d.weapon === 'pistol' ? 'secondary' : 'primary') : 'bag';
        else if (e.shiftKey && (from === 'bag' || from === 'pouch')) to = from === 'bag' ? 'pouch' : 'bag';
        else to = 'stash';
        this.send({ t: 'move', from, uid: it.uid, to });
      },
      // Clic droit deux fois pour vendre (ou glisser sur la zone de vente des marchands)
      context: (it: ItemInst) => {
        const now = performance.now();
        if (this.sellArmed && this.sellArmed.uid === it.uid && now < this.sellArmed.until) { this.sellArmed = null; this.send({ t: 'sell', from, uid: it.uid }); return; }
        this.sellArmed = { uid: it.uid, until: now + 2500 };
        const v = it.free ? 0 : ITEMS[it.id]!.value * it.n;
        this.toast(v ? `Clic droit à nouveau pour vendre ${ITEMS[it.id]!.name} : ${cr(v)}` : 'Clic droit à nouveau pour jeter cet objet du kit civil');
      },
    };
  }

  private render() {
    const p = this.profile;
    if (!p) return;
    $('hoName').innerHTML = `${p.avatar ? `<img src="${p.avatar}" alt="">` : ''}${esc(p.name)}`;
    $('hoCredits').textContent = cr(p.credits);
    // Niveau et progression vers le suivant
    const lvl = levelFromXp(p.xp), lo = xpFor(lvl), hi = xpFor(lvl + 1);
    const frac = lvl >= MAX_LEVEL ? 1 : (p.xp - lo) / (hi - lo);
    const xpText = lvl >= MAX_LEVEL ? 'Niveau maximal' : `${(p.xp - lo).toLocaleString('fr-FR')} / ${(hi - lo).toLocaleString('fr-FR')} XP`;
    $('hoLevel').innerHTML = `<b title="Niveau du récupérateur">${lvl}</b><span><strong>Niveau ${lvl}</strong><small>${xpText}</small><i><em style="width:${(frac * 100).toFixed(1)}%"></em></i></span>`;
    const st = p.stats, rate = st.raids ? Math.round((st.extracts / st.raids) * 100) : 0;
    $('hoStats').innerHTML = [['Raids', st.raids], ['Extractions', st.extracts], ['Taux de survie', `${rate} %`], ['Morts', st.deaths], ['Éliminations', st.kills],
      ['Niveau', lvl], ['Expérience totale', `${p.xp.toLocaleString('fr-FR')} XP`]].map(([k, v]) => `<div><span>${k}</span><b>${v}</b></div>`).join('');
    const used = (g: Grid) => g.items.reduce((s, i) => s + ITEMS[i.id]!.w * ITEMS[i.id]!.h, 0);
    const risk = gridValue(p.loadout.bag) + [p.loadout.primary, p.loadout.secondary, p.loadout.helmet, p.loadout.vest, p.loadout.backpack].reduce((s, w) => s + (w && !w.free ? ITEMS[w.id]!.value : 0), 0);
    const riskText = risk ? `Valeur emportée, à risque : <b>${cr(risk)}</b>` : 'Rien d’équipé : tu partiras avec le kit civil (pistolet, 30 cartouches, un bandage).';

    if (this.tab === 'gear') {
      renderSlot($('slotPrimary'), p.loadout.primary, 'primary', 'Arme principale', this.handlers('primary'));
      renderSlot($('slotSecondary'), p.loadout.secondary, 'secondary', 'Pistolet', this.handlers('secondary'));
      renderSlot($('slotHelmet'), p.loadout.helmet, 'helmet', 'Casque', this.handlers('helmet'));
      renderSlot($('slotVest'), p.loadout.vest, 'vest', 'Gilet', this.handlers('vest'));
      this.modButton($('slotPrimary'), 'primary', p.loadout.primary);
      this.modButton($('slotSecondary'), 'secondary', p.loadout.secondary);
      this.repairButton($('slotHelmet'), 'helmet', p.loadout.helmet);
      this.repairButton($('slotVest'), 'vest', p.loadout.vest);
      renderGrid($('bagGrid'), p.loadout.bag, 'bag', this.handlers('bag'));
      renderGrid($('pouchGrid'), p.loadout.pouch, 'pouch', this.handlers('pouch'));
      renderGrid($('stashGrid'), p.stash, 'stash', this.handlers('stash'));
      this.filterStash();
      $('stashCount').textContent = `(${used(p.stash)} / ${p.stash.w * p.stash.h})`;
      $('stashFill').innerHTML = `Valeur estimée : <b>${cr(gridValue(p.stash))}</b>`;
      const bagUsed = used(p.loadout.bag), bagCap = p.loadout.bag.w * p.loadout.bag.h;
      $('bagFill').textContent = `${bagUsed} / ${bagCap}`;
      renderSlot($('bagCard'), p.loadout.backpack, 'backpack', 'Sac à dos', this.handlers('backpack'));
      $('bagTitle').textContent = p.loadout.backpack ? ITEMS[p.loadout.backpack.id]!.name : 'Poches';
      $('hoRisk').innerHTML = riskText;
      // Masse portée, santé, protection, valeur à risque
      const l = p.loadout;
      const carried = [l.primary, l.secondary, l.helmet, l.vest, l.backpack].filter(Boolean) as ItemInst[];
      const kg = [...carried, ...l.bag.items, ...l.pouch.items].reduce((s, it) => s + weightOf(it), 0);
      const cls = (it: ItemInst | null) => (it ? ITEMS[it.id]!.armor!.cls : 0);
      $('hoVitals').innerHTML = `<span title="Masse portée">${VITAL.kg}<b>${kg.toFixed(1).replace('.', ',')}</b> / ${CARRY_KG} kg</span>`
        + `<span class="hp" title="Santé">${VITAL.hp}<b>${MAX_HP}</b> / ${MAX_HP}</span>`
        + `<span title="Classe de protection (casque · gilet)">${VITAL.shield}<b>${cls(l.helmet)} · ${cls(l.vest)}</b> classes</span>`
        + `<span class="val" title="Valeur emportée, perdue si tu meurs">${VITAL.coin}<b>${risk.toLocaleString('fr-FR')}</b> cr</span>`;
      this.updatePreviewWeapon();
    } else if (this.tab === 'traders') {
      const t = TRADERS[this.trader]!;
      $('traderList').innerHTML = TRADERS.map((tr, i) => `<button data-trader="${i}" class="${i === this.trader ? 'on' : ''}"><span class="badge">${tr.name.split(' ').pop()![0]}</span><span><b>${tr.name}</b><small>${tr.role}</small></span></button>`).join('');
      $('trName').textContent = t.name;
      $('trBio').textContent = t.bio;
      // Les offres au-dessus du niveau du récupérateur sont visibles mais verrouillées
      $('shop').innerHTML = t.offers.filter(id => this.prices[id]).map(id => {
        const d = ITEMS[id]!, price = this.prices[id]!, need = this.unlock[id] ?? 1;
        const n = d.stack > 1 ? `×${d.stack}` : '';
        if (lvl < need) return `<button class="locked" disabled><img src="${iconOf(id)}" alt=""><span class="nm">${esc(d.name)} <i>${n}</i></span><b>Niveau ${need}</b></button>`;
        return `<button data-buy="${id}" ${p.credits < price ? 'disabled' : ''}><img src="${iconOf(id)}" alt=""><span class="nm">${esc(d.name)} <i>${n}</i></span><b>${cr(price)}</b></button>`;
      }).join('');
      renderGrid($('stashGrid2'), p.stash, 'stash', this.handlers('stash'));
    } else if (this.tab === 'base') {
      this.renderBase(p);
    } else if (this.tab === 'quests') {
      this.renderQuests(p);
    } else {
      $('raidRisk').innerHTML = riskText;
      const w = [p.loadout.primary, p.loadout.secondary].filter(Boolean).map(i => ITEMS[i!.id]!.name);
      $('raidLoadout').textContent = w.length ? w.join(' · ') : 'Kit civil';
    }
  }

  // ---------------------------------------------------------------- Planque : modules et fabrication

  private renderBase(p: ProfileView) {
    const h = p.hideout;
    if (!this.base) this.base = new BaseScene($('baseCanvas') as HTMLCanvasElement, $('baseTags'), $('baseLines') as unknown as SVGSVGElement,
      id => { this.baseSel = id; this.render(); }, () => this.tab === 'base' && !$('hideout').classList.contains('hidden'));
    this.base.setLevels(h.levels, this.baseSel);
    const need = (id: string, n: number) => {
      const have = countOf(p.stash, id);
      return `<span class="need ${have < n ? 'miss' : ''}" title="${esc(ITEMS[id]!.name)}"><img src="${iconOf(id)}" alt="">${Math.min(have, n)}/${n}</span>`;
    };
    const dur = (s: number) => (s >= 60 ? `${Math.round(s / 60)} min` : `${s} s`);
    const m = MODULES.find(x => x.id === this.baseSel)!;
    const lvl = levelOf(h, m.id), next = m.levels[lvl];
    const power = levelOf(h, 'generator') >= 1;
    let html = `<header class="bdHead"><i>${MODULE_ICON[m.id]}</i><h2>${esc(m.name)}</h2><span class="bdDash"></span></header>
      <div class="bdTop"><span class="bdLvl ${lvl ? '' : 'off'}">${lvl ? `Niveau ${lvl}` : 'À construire'}</span><img class="bdThumb" src="${this.base.thumbnail(m.id)}" alt=""></div>
      <p class="bdBio">${esc(m.bio)}</p>
      <h4 class="bdSec">Effets actuels</h4>
      <ul class="bdList">${lvl ? m.levels.slice(0, lvl).map(l => `<li>${esc(l.effect)}</li>`).join('') : '<li class="none">Aucun : le module n’est pas encore construit.</li>'}</ul>`;
    if (next) {
      const blocked = Object.entries(next.needs ?? {}).filter(([id, l]) => levelOf(h, id as ModuleId) < l!).map(([id, l]) => `${MODULES.find(x => x.id === id)!.name} niveau ${l}`);
      const ok = !blocked.length && p.credits >= next.credits && next.items.every(([id, n]) => countOf(p.stash, id) >= n);
      html += `<div class="bdNext"><h4>${lvl ? `Prochain niveau (${lvl + 1})` : 'Construction (niveau 1)'}</h4><ul><li>${esc(next.effect)}</li>${blocked.length ? `<li class="req">Requiert : ${esc(blocked.join(', '))}</li>` : ''}</ul></div>
        <h4 class="bdSec">Conditions d’amélioration</h4>
        <div class="bdCost"><span class="bdCr ${p.credits < next.credits ? 'miss' : ''}">${next.credits.toLocaleString('fr-FR')} CR</span>${next.items.map(([id, n]) => need(id, n)).join('')}</div>
        <button data-up="${m.id}" class="bdUp ${ok ? 'primary' : ''}" ${ok ? '' : 'disabled'}>${lvl ? `Améliorer vers niveau ${lvl + 1}` : 'Construire'}</button>`;
    } else html += '<div class="bdNext max"><h4>Niveau maximal</h4><ul><li>Toutes les améliorations de ce module sont faites.</li></ul></div>';
    // Ateliers : fabrication en cours, puis recettes (disponibles ou verrouillées)
    const recipes = RECIPES.filter(r => r.module === m.id);
    if (recipes.length) {
      const c = h.crafts[m.id], cur = c && recipeById(c.recipe);
      html += `<h4 class="bdSec">Recettes disponibles (${recipes.filter(r => lvl >= r.level).length})</h4>`;
      if (!power) html += '<p class="bdWarn">Le générateur doit être construit pour fabriquer.</p>';
      if (c && cur) html += `<div class="craft bdCraft" data-ends="${c.ends}" data-total="${craftTime(h, cur) * 1000}"><img src="${iconOf(cur.output[0])}" alt=""><span><b>${esc(cur.name)}</b> <small>×${cur.output[1]}</small><span class="bar"><i></i></span><span class="left"></span></span><button data-collect="${m.id}" class="primary hidden">Récupérer</button></div>`;
      html += '<div class="bdRecipes">' + recipes.map(r => {
        const open = lvl >= r.level;
        const can = power && open && !c && r.inputs.every(([id, n]) => countOf(p.stash, id) >= n);
        const ing = r.inputs.map(([id, n]) => `${n} × ${ITEMS[id]!.name}`).join(', ');
        return `<div class="bdRec ${open ? '' : 'off'}" title="${esc(ing)}"><img src="${iconOf(r.output[0])}" alt=""><span><b>${esc(r.name)}</b> <small>×${r.output[1]} · ${dur(craftTime(h, r))}${open ? '' : ` · niv. ${r.level}`}</small>${this.baseAll ? `<span class="bdIng">${r.inputs.map(([id, n]) => need(id, n)).join('')}</span>` : ''}</span>
          ${open ? `<button data-craft="${r.id}" ${can ? '' : 'disabled'}>Fabriquer</button>` : `<i class="bdLock">${QI.lock}</i>`}</div>`;
      }).join('') + '</div>';
      html += `<button data-all="1" class="bdAll">${this.baseAll ? 'Masquer les ingrédients' : 'Voir les ingrédients'}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="${this.baseAll ? 'M6 15l6-6 6 6' : 'M9 6l6 6-6 6'}"/></svg></button>`;
    }
    $('baseDetail').innerHTML = html;
    this.tickCrafts();
  }

  // Estompe les objets du coffre qui ne correspondent ni à la recherche (nom complet ou court) ni à la catégorie
  private filterStash() {
    const p = this.profile;
    if (!p) return;
    const byUid = new Map(p.stash.items.map(it => [String(it.uid), it.id]));
    const q = this.sQuery.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const plain = (t: string) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    document.querySelectorAll<HTMLElement>('#stashGrid .it[data-uid]').forEach(el => {
      const d = ITEMS[byUid.get(el.dataset.uid!) ?? '']!;
      if (!d) return;
      const ok = (!this.sCat || d.cat === this.sCat || (this.sCat === 'armor' && d.cat === 'bag')) && (!q || plain(d.name).includes(q) || plain(d.short).includes(q));
      el.classList.toggle('dim', !ok);
    });
  }

  private tickCrafts() {
    if (this.tab !== 'base' || $('hideout').classList.contains('hidden')) return;
    const now = Date.now() + this.clock;
    document.querySelectorAll<HTMLElement>('#baseDetail .craft').forEach(el => {
      const ends = Number(el.dataset.ends), total = Number(el.dataset.total), left = Math.max(0, ends - now);
      (el.querySelector('.bar i') as HTMLElement).style.width = `${(100 * (1 - left / total)).toFixed(1)}%`;
      const s = Math.ceil(left / 1000);
      el.querySelector('.left')!.textContent = left ? `Encore ${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s` : 'Terminé';
      el.classList.toggle('ready', !left);
      el.querySelector('button')!.classList.toggle('hidden', left > 0);
    });
  }

  // ---------------------------------------------------------------- Contrats

  private questState(p: ProfileView, q: Quest): QState {
    if (p.quests.done.includes(q.id)) return 'done';
    if (q.id in p.quests.active) return 'active';
    return q.after && !p.quests.done.includes(q.after) ? 'locked' : 'open';
  }
  private questProgress(p: ProfileView, q: Quest) {
    const g = q.goal;
    return { have: Math.min(g.n, g.kind === 'deliver' ? countOf(p.stash, g.item) : p.quests.active[q.id] ?? 0), n: g.n };
  }
  private goalText(q: Quest) {
    const g = q.goal;
    return g.kind === 'deliver' ? `Livrer ${g.n} × ${ITEMS[g.item]!.name}` : g.kind === 'infected' ? `Tuer ${g.n} infectés` : g.kind === 'bandit' ? `Éliminer ${g.n} pillards` : 'Éliminer le capitaine Varenne';
  }
  private questArt(q: Quest, big = false) {
    const g = q.goal;
    if (g.kind === 'deliver') return `<span class="qArt item${big ? ' big' : ''}"><img src="${iconOf(g.item)}" alt=""></span>`;
    return `<span class="qArt${big ? ' big' : ''}"><svg viewBox="0 0 160 100" preserveAspectRatio="xMidYMid slice" aria-hidden="true">${QUEST_ART[g.kind]}</svg></span>`;
  }
  private rewardHtml(q: Quest) {
    return `<span class="qCr">${QI.cash}<b>${cr(q.reward.credits).toUpperCase()}</b></span>` + (q.reward.items ?? []).map(([id, n]) => `<span class="qItem"><img src="${iconOf(id)}" alt="" title="${esc(ITEMS[id]!.name)}">×${n}</span>`).join('');
  }

  private renderQuests(p: ProfileView) {
    const st = (q: Quest) => this.questState(p, q);
    // Partenaires : portrait, réputation et niveau
    $('qPartners').innerHTML = TRADERS.filter(tr => tr.id in PORTRAITS).map(tr => { // partenaires qui donnent des contrats
      const pts = repOf(p.quests, tr.id as Quest['trader']), lv = repLevel(pts);
      const frac = lv.to ? (pts - lv.from) / (lv.to - lv.from) : 1;
      return `<button class="qp${this.qTrader === tr.id ? ' on' : ''}" data-trader="${tr.id}"><span class="qpPic"><svg viewBox="0 0 120 120" aria-hidden="true">${PORTRAITS[tr.id]}</svg></span>
        <span class="qpBody"><span class="qpTop"><b>${esc(tr.name)}</b>${svgIcon(TRADER_ICON[tr.id]!)}</span><small>${esc(tr.role)}</small>
        <span class="qpRep"><em>Réputation</em><strong>${ROMAN[lv.level]}</strong><span>${lv.to ? `${pts - lv.from} / ${lv.to - lv.from}` : 'max'}</span></span><i><u style="width:${(frac * 100).toFixed(0)}%"></u></i></span></button>`;
    }).join('');
    // Liste : filtre, partenaire choisi, tri
    const all = QUESTS.filter(q => !this.qTrader || q.trader === this.qTrader);
    $('qnAll').textContent = `(${all.length})`;
    $('qnDone').textContent = `(${all.filter(q => st(q) === 'done').length})`;
    $('qnLocked').textContent = `(${all.filter(q => st(q) === 'locked').length})`;
    document.querySelectorAll<HTMLElement>('#qTabs [data-f]').forEach(b => b.classList.toggle('on', b.dataset.f === this.qFilter));
    const order: Record<QState, number> = { active: 0, open: 1, done: 2, locked: 3 };
    const sort = ($('qSort') as HTMLSelectElement).value;
    const list = all.filter(q => this.qFilter === 'all' || st(q) === this.qFilter).sort((a, b) =>
      sort === 'reward' ? b.reward.credits - a.reward.credits : sort === 'trader' ? a.trader.localeCompare(b.trader) || order[st(a)] - order[st(b)] : order[st(a)] - order[st(b)]);
    if (!this.qSel || !QUESTS.some(q => q.id === this.qSel)) this.qSel = list[0]?.id ?? null;
    const track = tracked();
    $('questList').innerHTML = list.map(q => {
      const s = st(q), pr = this.questProgress(p, q);
      const status = s === 'done' ? `<span class="qs done">${QI.check}Terminé</span>`
        : s === 'locked' ? `<span class="qs locked">${QI.lock}Verrouillé</span><small>Après « ${esc(QUESTS.find(x => x.id === q.after)!.title)} »</small>`
        : s === 'active' ? `<span class="qs active">${QI.clock}En cours</span><span class="qBar"><i style="width:${(pr.have / pr.n) * 100}%"></i></span><small>${pr.have} / ${pr.n}</small>`
        : `<span class="qs open">Disponible</span>`;
      return `<div class="qRow ${s}${q.id === this.qSel ? ' sel' : ''}" data-q="${q.id}">${this.questArt(q)}
        <div class="qMain"><h4>${s === 'done' ? QI.check : ''}${esc(q.title)}${track.has(q.id) ? '<em class="qTracked">suivi</em>' : ''}</h4><p>${esc(q.brief)}</p><span class="qObj">${QI.target}${esc(this.goalText(q))}</span></div>
        <div class="qRew">${this.rewardHtml(q)}</div><div class="qStat">${status}</div></div>`;
    }).join('') || '<p class="qEmpty">Aucune mission ici.</p>';
    // Fiche détaillée de la mission choisie
    const q = QUESTS.find(x => x.id === this.qSel);
    if (!q) { $('qDetail').innerHTML = ''; return; }
    const s = st(q), pr = this.questProgress(p, q), tr = TRADERS.find(t => t.id === q.trader)!;
    const pts = repOf(p.quests, q.trader), lv = repLevel(pts);
    const badge = s === 'done' ? `<span class="qBadge done">${QI.check}Terminé</span>` : s === 'active' ? `<span class="qBadge active">${QI.clock}En cours</span>` : s === 'locked' ? `<span class="qBadge locked">${QI.lock}Verrouillé</span>` : '<span class="qBadge open">Disponible</span>';
    const ready = s === 'active' && pr.have >= pr.n;
    $('qDetail').innerHTML = `<header class="qdHead"><span class="qdPath">${svgIcon(TRADER_ICON[tr.id]!)}Mission <i>/</i> ${esc(tr.name)}</span>${badge}</header>
      <div class="qdHero">${this.questArt(q, true)}<h2>${esc(q.title)}</h2></div>
      <p class="qdBrief">${esc(q.brief)}</p>
      <h4 class="qdSec">${QI.clock}Objectif</h4>
      <div class="qdObj${pr.have >= pr.n && s !== 'open' && s !== 'locked' ? ' ok' : ''}"><i>${pr.have >= pr.n && s !== 'open' && s !== 'locked' ? QI.check : ''}</i><span>${esc(this.goalText(q))}</span><b>${s === 'open' || s === 'locked' ? `0 / ${pr.n}` : `${pr.have} / ${pr.n}`}</b></div>
      <div class="qdInfo"><div>${QI.pin}<span><small>Zone</small>Saint-Aubry<br>${esc(q.where)}</span></div><div>${QI.gear}<span><small>Équipement conseillé</small>${q.gear.map(esc).join(' · ')}</span></div></div>
      <h4 class="qdSec plain">Récompenses</h4>
      <div class="qdRewards"><span>${QI.cash}<b class="gold">${cr(q.reward.credits).toUpperCase()}</b></span>${(q.reward.items ?? []).map(([id, n]) => `<span><img src="${iconOf(id)}" alt=""><b>×${n}</b></span>`).join('')}<span>${QI.xp}<b>+ ${q.reward.xp.toLocaleString('fr-FR')} XP</b></span></div>
      <div class="qdRep">${QI.hand}<span>Réputation : <b>${esc(tr.name)}</b></span><em>+ ${q.rep}</em><i><u style="width:${lv.to ? ((pts - lv.from) / (lv.to - lv.from)) * 100 : 100}%"></u></i><small>${lv.to ? `${pts - lv.from} / ${lv.to - lv.from}` : 'max'} (${ROMAN[lv.level]})</small></div>
      <footer class="qdBtns"><button data-act="track" class="qTrack${track.has(q.id) ? ' on' : ''}" ${s === 'done' || s === 'locked' ? 'disabled' : ''}>${QI.flag}${track.has(q.id) ? 'Suivi' : 'Suivre'}</button>
      ${s === 'open' ? '<button data-act="accept" class="primary">Accepter</button>' : `<button data-act="turnin" class="${ready ? 'primary' : ''}" ${ready ? '' : 'disabled'}>${QI.check}Remettre</button>`}</footer>`;
  }

  // ---------------------------------------------------------------- Personnage en 3D

  private startPreview() {
    if (this.preview) return;
    const canvas = $('charView') as HTMLCanvasElement;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true;
    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xe8eef5, 0x2a2622, 1.2));
    const key = new THREE.DirectionalLight(0xfff0dc, 2.4);
    key.position.set(2, 4, 3);
    key.castShadow = true;
    const rim = new THREE.DirectionalLight(0xe8a33d, 1.4);
    rim.position.set(-3, 2, -3);
    scene.add(key, rim);
    const floor = new THREE.Mesh(new THREE.CircleGeometry(0.9, 48), new THREE.MeshStandardMaterial({ color: 0x15171a, roughness: 1, transparent: true, opacity: 0.85 }));
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);
    const camera = new THREE.PerspectiveCamera(26, 1, 0.1, 50);
    camera.position.set(0, 1.15, 5.1);
    camera.lookAt(0, 0.85, 0);
    const actor = new Actor(scene, survivorModel(0));
    this.preview = { renderer, scene, camera, actor, yaw: 0.5, drag: null };
    canvas.onpointerdown = e => { this.preview!.drag = e.clientX; canvas.setPointerCapture(e.pointerId); };
    canvas.onpointermove = e => { const pv = this.preview!; if (pv.drag !== null) { pv.yaw += (e.clientX - pv.drag) * 0.01; pv.drag = e.clientX; } };
    canvas.onpointerup = () => { this.preview!.drag = null; };
    this.updatePreviewWeapon();
    let last = performance.now();
    const loop = (now: number) => {
      requestAnimationFrame(loop);
      if ($('hideout').classList.contains('hidden') || this.tab !== 'gear') return;
      const pv = this.preview!, dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const r = canvas.getBoundingClientRect();
      if (canvas.width !== Math.round(r.width * renderer.getPixelRatio())) { renderer.setSize(r.width, r.height, false); camera.aspect = r.width / r.height; camera.updateProjectionMatrix(); }
      if (pv.drag === null) pv.yaw += dt * 0.25;
      pv.actor?.update(dt, { x: 0, z: 0, angle: Math.PI / 2 - pv.yaw, moving: false, sprint: false, crouch: false, dead: false, weapon: this.previewWeapon(), mods: this.previewMods(), helmet: this.profile?.loadout.helmet?.id ?? null, vest: this.profile?.loadout.vest?.id ?? null, pack: !!this.profile?.loadout.backpack });
      renderer.render(scene, camera);
    };
    requestAnimationFrame(loop);
  }
  private previewWeapon() {
    const l = this.profile?.loadout;
    const it = l?.primary ?? l?.secondary;
    return it ? ITEMS[it.id]!.weapon ?? null : null;
  }
  private previewMods() { const l = this.profile?.loadout; return (l?.primary ?? l?.secondary)?.mods; }
  private updatePreviewWeapon() {
    this.preview?.actor?.setWeapon(this.previewWeapon(), this.previewMods());
    this.preview?.actor?.setArmor(this.profile?.loadout.helmet?.id ?? null, this.profile?.loadout.vest?.id ?? null, this.profile?.loadout.backpack?.id ?? null);
  }

  // ---------------------------------------------------------------- Carte du raid

  // Carte photographiée du ciel (main.ts) + étiquettes des lieux et des extractions, placées en pourcentage
  setMapImage(url: string, at: (x: number, y: number) => [number, number], ratio: number) {
    if (!url) return;
    ($('mapWrap') as HTMLElement).style.aspectRatio = String(ratio);
    const img = $('mapImg') as HTMLImageElement;
    img.src = url;
    img.classList.remove('hidden');
    $('mapView').classList.add('hidden');
    const pct = (x: number, y: number) => { const [px, py] = at(x, y); return `left:${px.toFixed(2)}%;top:${py.toFixed(2)}%`; };
    const ico = (k: string) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${MAP_ICONS[k] ?? MAP_ICONS.pin}</svg>`;
    const places = MAP.buildings.filter(b => b.name !== 'Maison')
      .map(b => `<span class="mk" style="${pct((b.x0 + b.x1) / 2, (b.y0 + b.y1) / 2)}">${ico(NAME_ICON[b.name] ?? 'pin')}${esc(b.name)}</span>`);
    // Extraction : rond vert et nom, côté intérieur de la carte
    const exits = MAP.extracts.map(e => {
      const side = e.x > MAP.width * 0.7 ? 'left' : 'right';
      return `<span class="ex ${side}${e.cost ? ' paid' : ''}" style="${pct(e.x, e.y)}"><i>${RUN_ICON}</i><b>${esc(e.name)}${e.cost ? ` · ${e.cost.toLocaleString('fr-FR')} cr` : ''}</b></span>`;
    });
    $('mapMarks').innerHTML = places.join('') + exits.join('');
  }

  private drawMap() {
    const c = $('mapView') as HTMLCanvasElement;
    const k = 4; // pixels par mètre
    c.width = MAP.width * k; c.height = MAP.height * k;
    const g = c.getContext('2d')!;
    g.fillStyle = '#4c5640'; g.fillRect(0, 0, c.width, c.height);
    const COL: Record<string, string> = { asphalt: '#3a3c3e', parking: '#45474a', sidewalk: '#8d897f', concrete: '#7f7a70', gravel: '#5d554b', dirt: '#6b5d48', wood: '#6e5a45', tiles: '#7d7b74' };
    for (const f of MAP.floors) { g.fillStyle = COL[f.mat] ?? '#555'; g.fillRect(f.x0 * k, f.y0 * k, (f.x1 - f.x0) * k, (f.y1 - f.y0) * k); }
    for (const b of MAP.buildings) { g.fillStyle = '#' + b.roof.toString(16).padStart(6, '0'); g.fillRect(b.x0 * k, b.y0 * k, (b.x1 - b.x0) * k, (b.y1 - b.y0) * k); }
    g.fillStyle = '#23272b';
    for (const r of MAP.rects) if (r.kind === 'wall' && r.look === 'model' && r.x1 - r.x0 > 3) g.fillRect(r.x0 * k, r.y0 * k, (r.x1 - r.x0) * k, (r.y1 - r.y0) * k);
    g.font = '600 13px "Barlow Condensed", sans-serif';
    g.textAlign = 'center';
    for (const b of MAP.buildings) if (b.name !== 'Maison') {
      g.fillStyle = 'rgba(0,0,0,.55)';
      const w = g.measureText(b.name.toUpperCase()).width + 10;
      g.fillRect(((b.x0 + b.x1) / 2) * k - w / 2, ((b.y0 + b.y1) / 2) * k - 10, w, 18);
      g.fillStyle = '#efe9dc';
      g.fillText(b.name.toUpperCase(), ((b.x0 + b.x1) / 2) * k, ((b.y0 + b.y1) / 2) * k + 4);
    }
    for (const e of MAP.extracts) {
      g.strokeStyle = '#8fd47b'; g.lineWidth = 3;
      g.beginPath(); g.arc(e.x * k, e.y * k, e.r * k * 1.6, 0, Math.PI * 2); g.stroke();
      g.fillStyle = '#a6dc95';
      g.fillText(e.name.toUpperCase(), Math.min(c.width - 60, Math.max(60, e.x * k)), Math.min(c.height - 8, Math.max(16, e.y * k - e.r * k * 2)));
    }
    $('raidExtracts').innerHTML = MAP.extracts.map(e => `<li>${esc(e.name)}${e.cost ? ` <small>· ${e.cost.toLocaleString('fr-FR')} cr</small>` : ''}</li>`).join('');
  }
}
